import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Input,
  Select,
  Button,
  Dropdown,
  Space,
  message,
  Skeleton,
} from 'antd';
import {
  TeamOutlined,
  ThunderboltOutlined,
  StarOutlined,
  UserAddOutlined,
  SearchOutlined,
  DownloadOutlined,
  EllipsisOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  fetchUsers,
  fetchUserListStats,
  type UserListItem,
  type UserListStats,
} from '../../services/userManagementService';
import { AddUserModal } from './AddUserModal';
import { formatVnDate, parseApiInstant } from '../../utils/vnDateTime';

const ROUNDED_CARD_STYLE: React.CSSProperties = {
  borderRadius: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};
const GAP = 24;

function getInitials(name: string | undefined | null): string {
  if (!name || !name.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const AVATAR_COLORS = ['#2563eb', '#7c3aed', '#047857', '#ea580c', '#db2777'];
function avatarColor(name: string | undefined | null): string {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h << 5) - h + name.charCodeAt(i);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function formatJoinedDate(created_at: string): {
  line1: string;
  line2: string;
} {
  const line1 = formatVnDate(created_at, 'D MMM');
  const year = parseApiInstant(created_at)?.format('YYYY') ?? '';
  if (line1 === '—') return { line1: '–', line2: '' };
  return { line1, line2: year };
}

function statusPill(isActive: boolean): 'Active' | 'Inactive' | 'Suspended' {
  return isActive ? 'Active' : 'Inactive';
}

export default function UserManagementList() {
  const navigate = useNavigate();
  const [data, setData] = useState<UserListItem[]>([]);
  const [stats, setStats] = useState<UserListStats>({
    totalUsers: 0,
    activeToday: 0,
    loyaltyPointsIssued: 0,
    newSignups: 0,
  });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(5);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined
  );
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [addModalVisible, setAddModalVisible] = useState(false);

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchUsers({
        page,
        limit,
        search,
        shadowbanned: statusFilter === 'shadowbanned',
      });
      if (res.success && res.data) {
        setData(res.data as UserListItem[]);
        const totalFromApi = res.pagination?.total ?? 0;
        setTotal(totalFromApi);
      }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      message.error(ax.response?.data?.message || 'Failed to load users');
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, statusFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setStatsLoading(true);
      try {
        const s = await fetchUserListStats();
        if (!cancelled) setStats(s);
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setPage(1);
  };

  const handleOpenAddUser = () => setAddModalVisible(true);

  // Debounced search (match existing pattern)
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const avgLoyaltyPerUser =
    stats.totalUsers > 0
      ? Math.round(stats.loyaltyPointsIssued / stats.totalUsers)
      : 0;

  return (
    <div
      style={{
        padding: GAP,
        maxWidth: 1280,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: GAP,
        background: 'transparent',
      }}
    >
      <header className="merchant-detail-header">
        <h1 className="merchant-detail-title">Quản lý người dùng</h1>
        <nav className="merchant-detail-breadcrumb">
          <Link to="/">Trang chủ</Link>
          <span className="merchant-detail-breadcrumb-sep">&gt;</span>
          <span className="merchant-detail-breadcrumb-current">
            Quản lý người dùng
          </span>
        </nav>
      </header>

      {/* 4 KPI cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: GAP,
        }}
      >
        <Card style={ROUNDED_CARD_STYLE} bodyStyle={{ padding: 20 }}>
          {statsLoading ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <div>
                <div
                  style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}
                >
                  Tổng số người dùng
                </div>
                <div
                  style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}
                >
                  {stats.totalUsers.toLocaleString()}
                </div>
                <div style={{ fontSize: 12, color: '#22c55e', marginTop: 4 }}>
                  +12% this month
                </div>
              </div>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: '#dbeafe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TeamOutlined style={{ fontSize: 22, color: '#2563eb' }} />
              </div>
            </div>
          )}
        </Card>
        <Card style={ROUNDED_CARD_STYLE} bodyStyle={{ padding: 20 }}>
          {statsLoading ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <div>
                <div
                  style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}
                >
                  Active Today
                </div>
                <div
                  style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}
                >
                  {stats.activeToday.toLocaleString()}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  8% of total base
                </div>
              </div>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: '#dcfce7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ThunderboltOutlined
                  style={{ fontSize: 22, color: '#16a34a' }}
                />
              </div>
            </div>
          )}
        </Card>
        <Card style={ROUNDED_CARD_STYLE} bodyStyle={{ padding: 20 }}>
          {statsLoading ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <div>
                <div
                  style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}
                >
                  Loyalty Points Issued
                </div>
                <div
                  style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}
                >
                  {stats.loyaltyPointsIssued >= 1e6
                    ? `${(stats.loyaltyPointsIssued / 1e6).toFixed(1)}M`
                    : stats.loyaltyPointsIssued.toLocaleString()}
                </div>
                <div style={{ fontSize: 12, color: '#2563eb', marginTop: 4 }}>
                  Avg{' '}
                  {stats.totalUsers > 0
                    ? Math.round(
                        stats.loyaltyPointsIssued / stats.totalUsers
                      ).toLocaleString()
                    : 0}
                  /user
                </div>
              </div>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: '#ede9fe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <StarOutlined style={{ fontSize: 22, color: '#7c3aed' }} />
              </div>
            </div>
          )}
        </Card>
        <Card style={ROUNDED_CARD_STYLE} bodyStyle={{ padding: 20 }}>
          {statsLoading ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <div>
                <div
                  style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}
                >
                  New Sign-ups
                </div>
                <div
                  style={{ fontSize: 28, fontWeight: 700, color: '#111827' }}
                >
                  {stats.newSignups}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  Last 24 hours
                </div>
              </div>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: '#ffedd5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <UserAddOutlined style={{ fontSize: 22, color: '#ea580c' }} />
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Filters */}
      <Card style={ROUNDED_CARD_STYLE} bodyStyle={{ padding: 16 }}>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <div style={{ flex: '1 1 40%', minWidth: 200, maxWidth: 400 }}>
            <Input
              placeholder="Search by name, email, or ID..."
              prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
              style={{ borderRadius: 8 }}
            />
          </div>
          <Select
            placeholder="All Status"
            value={statusFilter ?? undefined}
            onChange={v => {
              setStatusFilter(v || undefined);
              setPage(1);
            }}
            style={{ width: 140, borderRadius: 8 }}
            allowClear
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
              { value: 'suspended', label: 'Suspended' },
              { value: 'shadowbanned', label: 'Shadowbanned (Ads)' },
            ]}
          />
          {/* <Button icon={<DownloadOutlined />} style={{ borderRadius: 8 }}>
            Export
          </Button> */}
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            style={{ borderRadius: 8 }}
            onClick={handleOpenAddUser}
          >
            + Add User
          </Button>
        </div>
      </Card>

      {/* Table */}
      <Card style={ROUNDED_CARD_STYLE} bodyStyle={{ padding: 0 }}>
        <Table<UserListItem>
          loading={loading}
          dataSource={data}
          rowKey="id"
          pagination={false}
          size="middle"
          style={{ borderRadius: 16, overflow: 'hidden' }}
          locale={{ emptyText: 'No users found' }}
          onRow={record => ({
            onClick: () => navigate(`/user-management/${record.id}`),
            style: { cursor: 'pointer' },
          })}
          columns={[
            {
              title: 'USER PROFILE',
              dataIndex: 'name',
              key: 'profile',
              render: (_, record) => (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    cursor: 'pointer',
                  }}
                  onClick={() => navigate(`/user-management/${record.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/user-management/${record.id}`);
                    }
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: avatarColor(record.name),
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: 14,
                    }}
                  >
                    {getInitials(record.name)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: '#111827' }}>
                      {record.name || '—'}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      ID: #USR-{record.id}
                    </div>
                  </div>
                </div>
              ),
            },
            {
              title: 'EMAIL ADDRESS',
              dataIndex: 'email',
              key: 'email',
              render: (v: string) => v || '—',
            },
            {
              title: 'LOYALTY POINTS',
              key: 'loyalty',
              align: 'center',
              render: (_: unknown, record: UserListItem) => {
                const points =
                  (record as UserListItem & { loyalty_points?: number })
                    .loyalty_points ?? avgLoyaltyPerUser;
                return (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <StarOutlined style={{ color: '#ea580c', fontSize: 14 }} />
                    {points.toLocaleString()}
                  </span>
                );
              },
            },
            {
              title: 'STATUS',
              dataIndex: 'is_active',
              key: 'status',
              align: 'center',
              render: (isActive: boolean) => {
                const status = statusPill(isActive);
                const bg =
                  status === 'Active'
                    ? '#dcfce7'
                    : status === 'Suspended'
                    ? '#fee2e2'
                    : '#f3f4f6';
                const color =
                  status === 'Active'
                    ? '#166534'
                    : status === 'Suspended'
                    ? '#b91c1c'
                    : '#4b5563';
                return (
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '4px 10px',
                      borderRadius: 9999,
                      fontSize: 12,
                      fontWeight: 500,
                      background: bg,
                      color,
                    }}
                  >
                    {status}
                  </span>
                );
              },
            },
            {
              title: 'ADS',
              key: 'shadowban',
              align: 'center',
              render: (_: unknown, record: UserListItem) =>
                record.is_shadowbanned ? (
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '4px 10px',
                      borderRadius: 9999,
                      fontSize: 12,
                      fontWeight: 500,
                      background: '#fef3c7',
                      color: '#92400e',
                    }}
                  >
                    Shadowban
                  </span>
                ) : (
                  <span style={{ color: '#9ca3af', fontSize: 12 }}>–</span>
                ),
            },
            {
              title: 'JOINED DATE',
              dataIndex: 'created_at',
              key: 'created_at',
              render: (v: string) => {
                const { line1, line2 } = formatJoinedDate(v);
                return (
                  <div>
                    <div style={{ fontWeight: 500, color: '#111827' }}>
                      {line1}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      {line2}
                    </div>
                  </div>
                );
              },
            },
            {
              title: 'ACTIONS',
              key: 'actions',
              align: 'right',
              width: 80,
              render: (_, record) => (
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'view',
                        label: 'View Details',
                        onClick: () =>
                          navigate(`/user-management/${record.id}`),
                      },
                    ],
                  }}
                  trigger={['click']}
                  placement="bottomRight"
                >
                  <Button
                    type="text"
                    icon={<EllipsisOutlined />}
                    style={{ color: '#6b7280' }}
                    aria-label="Actions"
                    onClick={e => e.stopPropagation()}
                  />
                </Dropdown>
              ),
            },
          ]}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderTop: '1px solid #f0f0f0',
          }}
        >
          <span style={{ fontSize: 14, color: '#6b7280' }}>
            Showing {start} to {end} of {total} results
          </span>
          <Space>
            <Button
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              disabled={page * limit >= total}
              onClick={() => setPage(p => p + 1)}
            >
              Next
            </Button>
          </Space>
        </div>
      </Card>

      <AddUserModal
        open={addModalVisible}
        onClose={() => setAddModalVisible(false)}
        onSuccess={fetchList}
      />
    </div>
  );
}
